import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, Clock, Ticket, Users } from 'lucide-react';

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const isManager = hasPermission('schedule.create');

  useEffect(() => {
    API.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const cards = isManager
    ? [
        { label: 'Turnos Hoy', value: stats?.shifts_today ?? '-', icon: CalendarDays, color: 'text-amber-500 bg-amber-500/10' },
        { label: 'Horas Semana', value: stats?.week_hours ?? '-', icon: Clock, color: 'text-emerald-400 bg-emerald-400/10' },
        { label: 'Tickets Pendientes', value: stats?.tickets_pending ?? '-', icon: Ticket, color: 'text-blue-400 bg-blue-400/10' },
        { label: 'Usuarios Activos', value: stats?.total_users ?? '-', icon: Users, color: 'text-purple-400 bg-purple-400/10' },
      ]
    : [
        { label: 'Mis Turnos Hoy', value: stats?.shifts_today ?? '-', icon: CalendarDays, color: 'text-amber-500 bg-amber-500/10' },
        { label: 'Mis Horas Semana', value: stats?.week_hours ?? '-', icon: Clock, color: 'text-emerald-400 bg-emerald-400/10' },
        { label: 'Mis Tickets', value: stats?.tickets_pending ?? '-', icon: Ticket, color: 'text-blue-400 bg-blue-400/10' },
      ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-50 tracking-tight">
          Bienvenido, {user?.name}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {isManager ? 'Panel de gestion de La Finka' : 'Tu panel personal'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="glass-card" data-testid={`stat-card-${i}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">{c.label}</CardTitle>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.color}`}>
                <c.icon className="w-4 h-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold text-zinc-50">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base font-medium text-zinc-300">Accesos Rapidos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {isManager ? (
              <>
                <QuickLink to="/schedule" label="Gestionar Cuadrantes" icon={CalendarDays} />
                <QuickLink to="/tickets" label="Ver Tickets" icon={Ticket} />
                <QuickLink to="/reports" label="Generar Informes" icon={Clock} />
              </>
            ) : (
              <>
                <QuickLink to="/my-schedule" label="Mi Horario" icon={CalendarDays} />
                <QuickLink to="/hours" label="Mis Horas" icon={Clock} />
                <QuickLink to="/tickets" label="Mis Tareas" icon={Ticket} />
              </>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base font-medium text-zinc-300">Info del Sistema</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-zinc-400">
            <p><span className="text-zinc-500">Rol:</span> <span className="text-zinc-200">{user?.groups_data?.map(g => g.name).join(', ') || '-'}</span></p>
            <p><span className="text-zinc-500">Email:</span> <span className="text-zinc-200">{user?.email}</span></p>
            <p><span className="text-zinc-500">Permisos:</span> <span className="text-zinc-200">{user?.permissions?.length || 0} activos</span></p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function QuickLink({ to, label, icon: Icon }) {
  return (
    <a href={to} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors group">
      <Icon className="w-4 h-4 text-amber-500/70 group-hover:text-amber-500" />
      <span className="text-sm text-zinc-300 group-hover:text-zinc-100">{label}</span>
    </a>
  );
}
